import React, { useCallback, useEffect, useState } from 'react'

import type { WorkbookBucketConfig, WorkbookDefinition } from './workbook.types'
import {
    usePropertyApplicationWorkbook,
    useSavePropertyApplicationWorkbook,
    useOrgApplicationWorkbook,
    useSaveOrgApplicationWorkbook,
} from './useApplicationWorkbookApi'

type Scope = 'property' | 'org'

interface UseApplicationWorkbookBuilderStateArgs {
    scope: Scope
    apartmentId?: number | null
    organizationId?: number | null
    workbookId: number | null | undefined
}

export function useApplicationWorkbookBuilderState({
    scope,
    apartmentId,
    organizationId,
    workbookId,
}: UseApplicationWorkbookBuilderStateArgs) {
    const propertyQuery = usePropertyApplicationWorkbook(
        scope === 'property' ? apartmentId : null,
        scope === 'property' ? workbookId : null,
    )
    const orgQuery = useOrgApplicationWorkbook(
        scope === 'org' ? organizationId : null,
        scope === 'org' ? workbookId : null,
    )

    const saveProperty = useSavePropertyApplicationWorkbook(
        apartmentId,
        workbookId,
    )
    const saveOrg = useSaveOrgApplicationWorkbook(organizationId, workbookId)

    const detail = scope === 'property' ? propertyQuery.data : orgQuery.data
    const isLoading =
        scope === 'property' ? propertyQuery.isLoading : orgQuery.isLoading
    const isEditable = detail?.is_editable ?? true

    const [
        definition,
        setDefinitionState,
    ] = useState<WorkbookDefinition | null>(null)
    const [dirty, setDirty] = useState(false)

    useEffect(() => {
        if (detail?.definition) {
            setDefinitionState(detail.definition)
            setDirty(false)
        }
    }, [detail?.definition, workbookId])

    const updateBucket = useCallback(
        (bucketId: string, patch: Partial<WorkbookBucketConfig>) => {
            setDefinitionState((prev) => {
                if (!prev) return prev
                return {
                    ...prev,
                    buckets: prev.buckets.map((bucket) =>
                        bucket.bucket_id === bucketId
                            ? { ...bucket, ...patch }
                            : bucket,
                    ),
                }
            })
            setDirty(true)
        },
        [],
    )

    const setDefinition = useCallback((next: WorkbookDefinition) => {
        setDefinitionState(next)
        setDirty(true)
    }, [])

    const save = useCallback(async () => {
        if (!definition) return
        if (scope === 'property') {
            await saveProperty.mutateAsync(definition)
        } else {
            await saveOrg.mutateAsync(definition)
        }
        setDirty(false)
    }, [definition, saveOrg, saveProperty, scope])

    return {
        detail,
        definition,
        isLoading,
        isEditable,
        dirty,
        updateBucket,
        setDefinition,
        save,
        isSaving: saveProperty.isLoading || saveOrg.isLoading,
    }
}
